// Real-mode Overview fetcher — the swap-in for getOverviewMock() once
// VITE_API_URL is configured (task [2.5]'s hook selects mock vs real).
// Calls fetch('/overview') with a RELATIVE path: the installed fetch-patch
// (src/lib/fetch-patch.ts) prefixes VITE_API_URL + '/api', injects the MSAL
// bearer, and throws the Response on non-2xx — so a successful await here is
// already a 2xx and we only validate the body shape.
//
// NOTE (per proposal risk table): bsg-email-scheduler's API is not stable yet,
// so this fetcher's runtime success against a real backend is untested by design.

import { z } from 'zod';

import {
	barDatumSchema,
	donutDatumSchema,
	groupedBarDatumSchema,
	linePointSchema,
	statTileSchema,
} from '@/api/schemas';
import type { OverviewData } from './mock';

// Overview-specific composite shapes — kept local (not in @/api/schemas, which
// holds only the genuinely shared chart primitives). Both reuse linePointSchema.
const providerVolumeSeriesSchema = z.object({
	provider: z.string(),
	points: z.array(linePointSchema),
});

const volumeHealthPointSchema = z.object({
	timestamp: z.string(),
	application: z.string(),
	sent: z.number(),
	delivered: z.number(),
	failed: z.number(),
});

const overviewDataSchema = z.object({
	statTiles: z.array(statTileSchema),
	volumeByProvider: z.array(barDatumSchema),
	volumeByProviderOverTime: z.array(providerVolumeSeriesSchema),
	statusSplit: z.array(donutDatumSchema),
	volumeOverTime: z.array(linePointSchema),
	weeklyBreakdown: z.array(groupedBarDatumSchema),
	volumeAndHealth: z.array(volumeHealthPointSchema),
});

export async function getOverview(): Promise<OverviewData> {
	const response = await fetch('/overview');
	const json: unknown = await response.json();
	return overviewDataSchema.parse(json);
}
